import { useState, useEffect, useRef, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Button, Divider, Input, Typography } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  RedoOutlined,
  DownloadOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from "@ant-design/icons";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PdfViewer = ({ 
  pdfUrls, 
  initialPage = 1,
  zoomable = false,
  rotatable = false,
  fullscreenable = false,
  downloadable = false,
  showDocumentNav = true,
  currentDocIndex: controlledDocIndex,
  onDocumentIndexChange,
  noBorder = false,
}) => {
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [inputPage, setInputPage] = useState(initialPage.toString());
  const [pageNumber, setPageNumber] = useState(initialPage);
  const [numPages, setNumPages] = useState(null);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true);
  const activeDocIndex = controlledDocIndex ?? currentDocIndex;
  const hideControlsTimerRef = useRef(null);
  const setDocIndex = (nextIndex) => {
    if (onDocumentIndexChange) {
      onDocumentIndexChange(nextIndex);
    } else {
      setCurrentDocIndex(nextIndex);
    }
  };

  const scrollContainerRef = useRef(null);
  const pageRefs = useRef([]);

  const clearHideControlsTimer = useCallback(() => {
    if (hideControlsTimerRef.current) {
      clearTimeout(hideControlsTimerRef.current);
      hideControlsTimerRef.current = null;
    }
  }, []);

  const scheduleControlsHide = useCallback(() => {
    clearHideControlsTimer();
    hideControlsTimerRef.current = setTimeout(() => {
      setControlsVisible(false);
    }, 2000);
  }, [clearHideControlsTimer]);

  const showControlsOnActivity = useCallback(() => {
    setControlsVisible(true);
    scheduleControlsHide();
  }, [scheduleControlsHide]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      showControlsOnActivity();
      if (e.key === "ArrowRight" && pageNumber < (numPages || 0)) {
        scrollToPage(pageNumber + 1);
      } else if (e.key === "ArrowLeft" && pageNumber > 1) {
        scrollToPage(pageNumber - 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pageNumber, numPages, showControlsOnActivity]);

  useEffect(() => {
    scheduleControlsHide();
    return () => clearHideControlsTimer();
  }, [scheduleControlsHide, clearHideControlsTimer]);

  useEffect(() => {
    if (!numPages || !scrollContainerRef.current) return;

    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const scrollTop = container.scrollTop;
      let currentPage = 1;

      for (let i = 0; i < pageRefs.current.length; i++) {
        const pageRef = pageRefs.current[i];
        if (pageRef) {
          const pageTop = pageRef.offsetTop - container.offsetTop;
          if (pageTop <= scrollTop + 100) { // Threshold for when page is considered "current"
            currentPage = i + 1;
          } else {
            break;
          }
        }
      }

      setPageNumber(currentPage);
      setInputPage(currentPage.toString());
    };

    const container = scrollContainerRef.current;
    container.addEventListener('scroll', handleScroll);

    // Initial call to set page number
    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [numPages]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    pageRefs.current = new Array(numPages).fill(null);
    // Scroll to initial page after a brief delay to ensure refs are ready
    setTimeout(() => {
      if (initialPage > 1 && initialPage <= numPages) {
        scrollToPage(initialPage);
      }
    }, 100);
  };

  // Reset page when switching documents
  useEffect(() => {
    setPageNumber(1);
    setInputPage("1");
    setNumPages(null);
  }, [activeDocIndex]);

  // Respond to prop updates for initialPage (e.g., same pdf with different page)
  useEffect(() => {
    if (!numPages) return;
    const target = Math.max(1, Math.min(initialPage || 1, numPages));
    // Only act if prop differs from the current visible page
    if (target !== pageNumber) {
      // Delay slightly to ensure refs/layout are ready
      setTimeout(() => {
        scrollToPage(target);
      }, 0);
    }
  }, [initialPage, pdfUrls, numPages]);

  const handlePageInputChange = (e) => {
    setInputPage(e.target.value);
  };

  const handlePageInputBlur = () => {
    const page = parseInt(inputPage);
    if (page >= 1 && page <= (numPages || 0)) {
      scrollToPage(page);
    } else {
      setInputPage(pageNumber.toString());
    }
  };

  const handlePageInputKeyPress = (e) => {
    if (e.key === "Enter") {
      handlePageInputBlur();
    }
  };

  const scrollToPage = (page) => {
    const pageRef = pageRefs.current[page - 1];
    if (pageRef && scrollContainerRef.current) {
      setPageNumber(page);
      setInputPage(page.toString());
      pageRef.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 3.0));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const containerStyle = {
    position: isFullscreen ? "fixed" : "relative",
    inset: isFullscreen ? 0 : "auto",
    zIndex: isFullscreen ? 1100 : "auto",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    background: "#f5f7fa",
    border: noBorder ? "none" : "1px solid #d9d9d9",
    borderRadius: noBorder || isFullscreen ? 0 : 8,
  };

  const floatingButtonStyle = {
    opacity: controlsVisible ? 1 : 0,
    pointerEvents: controlsVisible ? "auto" : "none",
    transition: "opacity 0.3s ease",
  };

  return (
    <div
      style={containerStyle}
      onMouseMove={showControlsOnActivity}
      onMouseDown={showControlsOnActivity}
      onTouchStart={showControlsOnActivity}
      onWheel={showControlsOnActivity}
    >
      <div
        style={{
          flex: 1,
          overflow: "auto",
          background: "#eef1f5",
          padding: "16px 16px 96px",
        }}
        ref={scrollContainerRef}
      >
        <Document
          file={pdfUrls[activeDocIndex]?.url}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 80, color: "#8c8c8c" }}>
              Loading PDF...
            </div>
          }
          error={
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 80, color: "#ff4d4f" }}>
              Failed to load PDF. Please check the URL.
            </div>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
            {numPages && Array.from({ length: numPages }, (_, index) => (
              <div
                key={index + 1}
                ref={(el) => { pageRefs.current[index] = el; }}
                style={{
                  background: "#fff",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
                  padding: 16,
                }}
              >
                <Page
                  pageNumber={index + 1}
                  scale={scale}
                  rotate={rotation}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  loading={
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 80, color: "#8c8c8c" }}>
                      Loading page...
                    </div>
                  }
                />
              </div>
            ))}
          </div>
        </Document>
      </div>

      <div
        style={{
          position: "absolute",
          left: "50%",
          bottom: 16,
          transform: "translateX(-50%)",
          zIndex: 20,
          border: "1px solid #d9d9d9",
          borderRadius: 999,
          background: "rgba(255, 255, 255, 0.96)",
          boxShadow: "0 6px 20px rgba(0, 0, 0, 0.16)",
          padding: "8px 12px",
          ...floatingButtonStyle,
        }}
        onMouseEnter={() => {
          setControlsVisible(true);
          clearHideControlsTimer();
        }}
        onMouseLeave={scheduleControlsHide}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {showDocumentNav && pdfUrls.length > 1 && (
            <>
              <Button
                type="text"
                shape="circle"
                icon={<LeftOutlined />}
                onClick={() => setDocIndex(Math.max(activeDocIndex - 1, 0))}
                disabled={activeDocIndex <= 0}
                title="Previous document"
              />
              <Typography.Text style={{ minWidth: 48, textAlign: "center", fontSize: 12, color: "#595959" }}>
                {activeDocIndex + 1}/{pdfUrls.length}
              </Typography.Text>
              <Button
                type="text"
                shape="circle"
                icon={<RightOutlined />}
                onClick={() => setDocIndex(Math.min(activeDocIndex + 1, pdfUrls.length - 1))}
                disabled={activeDocIndex >= pdfUrls.length - 1}
                title="Next document"
              />
              <Divider type="vertical" style={{ height: 24, margin: "0 4px" }} />
            </>
          )}

          <Button
            type="text"
            shape="circle"
            icon={<LeftOutlined />}
            onClick={() => scrollToPage(Math.max(pageNumber - 1, 1))}
            disabled={pageNumber <= 1}
            title="Previous page"
          />

          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 4px" }}>
            <Input
              value={inputPage}
              onChange={handlePageInputChange}
              onBlur={handlePageInputBlur}
              onKeyPress={handlePageInputKeyPress}
              style={{ width: 48, textAlign: "center", padding: "2px 6px" }}
            />
            <Typography.Text style={{ fontSize: 12, color: "#595959" }}>/ {numPages || "..."}</Typography.Text>
          </div>

          <Button
            type="text"
            shape="circle"
            icon={<RightOutlined />}
            onClick={() => scrollToPage(Math.min(pageNumber + 1, numPages || 0))}
            disabled={pageNumber >= (numPages || 0)}
            title="Next page"
          />

          {zoomable && (
            <>
              <Divider type="vertical" style={{ height: 24, margin: "0 4px" }} />
              <Button
                type="text"
                shape="circle"
                icon={<ZoomOutOutlined />}
                onClick={handleZoomOut}
                disabled={scale <= 0.5}
                title="Zoom out"
              />
              <Typography.Text style={{ minWidth: 44, textAlign: "center", fontSize: 12, color: "#595959" }}>
                {Math.round(scale * 100)}%
              </Typography.Text>
              <Button
                type="text"
                shape="circle"
                icon={<ZoomInOutlined />}
                onClick={handleZoomIn}
                disabled={scale >= 3.0}
                title="Zoom in"
              />
            </>
          )}

          {rotatable && (
            <Button
              type="text"
              shape="circle"
              icon={<RedoOutlined />}
              onClick={handleRotate}
              title="Rotate 90°"
            />
          )}

          {fullscreenable && (
            <Button
              type="text"
              shape="circle"
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            />
          )}

          {downloadable && (
            <a
              href={pdfUrls[activeDocIndex]?.url}
              download
              title="Download PDF"
              target="_blank"
              rel="noreferrer"
              style={{ display: "inline-flex" }}
            >
              <Button type="text" shape="circle" icon={<DownloadOutlined />} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default PdfViewer;
